import { useState } from "react";

type Bean = {
  id: string;
  name: string;
  origin: string;
  process: string;
  roast: "Light" | "Medium" | "Dark";
  notes: string[];
  price: number;
  image: string;
};

const BEANS: Bean[] = [
  {
    id: "b1",
    name: "Yirgacheffe Konga",
    origin: "Ethiopia",
    process: "Washed",
    roast: "Light",
    notes: ["Jasmine", "Bergamot", "Stone Fruit"],
    price: 24,
    image:
      `${import.meta.env.BASE_URL}localized/bean-yirgacheffe-01.jpg`,
  },
  {
    id: "b2",
    name: "Huila Reserve",
    origin: "Colombia",
    process: "Honey",
    roast: "Medium",
    notes: ["Milk Chocolate", "Toffee", "Red Apple"],
    price: 22,
    image:
      `${import.meta.env.BASE_URL}localized/bean-huila-01.jpg`,
  },
  {
    id: "b3",
    name: "Antigua Highlands",
    origin: "Guatemala",
    process: "Fully Washed",
    roast: "Medium",
    notes: ["Dark Cocoa", "Hazelnut", "Brown Sugar"],
    price: 21,
    image:
      `${import.meta.env.BASE_URL}localized/bean-antigua-01.jpg`,
  },
  {
    id: "b4",
    name: "Sidamo Anaerobic",
    origin: "Ethiopia",
    process: "Anaerobic Natural",
    roast: "Light",
    notes: ["Blueberry", "Rose", "Cane Sugar"],
    price: 28,
    image:
      `${import.meta.env.BASE_URL}localized/bean-sidamo-01.jpg`,
  },
  {
    id: "b5",
    name: "Nariño Sugarcane",
    origin: "Colombia",
    process: "Sugarcane Decaf",
    roast: "Medium",
    notes: ["Caramel", "Almond", "Baker&apos;s Chocolate"],
    price: 23,
    image:
      `${import.meta.env.BASE_URL}localized/bean-narino-01.jpg`,
  },
  {
    id: "b6",
    name: "Hambela Estate",
    origin: "Ethiopia",
    process: "Natural",
    roast: "Dark",
    notes: ["Dark Berry", "Molasses", "Cedar"],
    price: 26,
    image:
      `${import.meta.env.BASE_URL}localized/bean-hambela-01.jpg`,
  },
];

const DRINKS = [
  { name: "Pour Over", desc: "Single origin, six-minute brew, served black.", price: "$5.50" },
  { name: "Espresso", desc: "House blend, 18g in, 36g out, thirty seconds.", price: "$3.75" },
  { name: "Cortado", desc: "Equal parts espresso and steamed milk.", price: "$4.75" },
  { name: "Cappuccino", desc: "Espresso, velveted whole milk, a whisper of foam.", price: "$5.25" },
  { name: "Cold Brew", desc: "Steeped twenty hours. Chocolate and quiet strength.", price: "$5.75" },
  { name: "Miel Latte", desc: "Espresso, milk, wildflower honey, sea salt.", price: "$6.25" },
];

const ROAST_FILTERS = ["All", "Light", "Medium", "Dark"] as const;

export default function Coffee() {
  const [filter, setFilter] = useState<(typeof ROAST_FILTERS)[number]>("All");
  const filtered = filter === "All" ? BEANS : BEANS.filter((b) => b.roast === filter);

  return (
    <section id="coffee" className="w-full bg-background-50 py-24 md:py-32 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 reveal">
          <div>
            <span className="text-xs tracking-[0.35em] uppercase text-accent-700">The Shop</span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-primary-950 mt-4 leading-tight">
              Beans, <em>bagged with intention.</em>
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2 bg-background-100 p-1.5 rounded-full w-fit">
            {ROAST_FILTERS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setFilter(r)}
                className={`px-5 py-2 rounded-full text-xs tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap ${
                  filter === r
                    ? "bg-primary-900 text-background-50"
                    : "text-foreground-700 hover:text-primary-900"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div
          data-product-shop
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-24"
        >
          {filtered.map((bean, idx) => (
            <article
              key={bean.id}
              className="group flex flex-col bg-background-100 rounded-lg overflow-hidden reveal border border-background-200/60 hover:border-accent-400 transition-colors"
              style={{ transitionDelay: `${idx * 40}ms` }}
            >
              <div className="relative h-80 overflow-hidden bg-background-200">
                <img
                  src={bean.image}
                  alt={bean.name}
                  className="w-full h-full object-cover object-top transition-transform duration-1000 group-hover:scale-105"
                />
                <span className="absolute top-4 right-4 text-[10px] tracking-[0.3em] uppercase bg-background-50 text-primary-900 px-3 py-1.5 rounded-full">
                  {bean.roast}
                </span>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <span className="text-xs tracking-[0.25em] uppercase text-accent-700 mb-2">{bean.origin}</span>
                <h3 className="font-heading text-2xl text-primary-950 mb-3">{bean.name}</h3>
                <p className="text-xs text-foreground-600 mb-4">{bean.process}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {bean.notes.map((n) => (
                    <span
                      key={n}
                      className="text-[11px] px-3 py-1 rounded-full bg-secondary-100 text-secondary-900"
                    >
                      {n}
                    </span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-5">
                  <span className="font-heading text-2xl text-primary-950">${bean.price}</span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-900 text-background-50 text-xs tracking-widest uppercase hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
                    <span>Bag It</span>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Cafe Drink List */}
        <div className="reveal">
          <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="text-xs tracking-[0.35em] uppercase text-accent-700">Cafe Menu</span>
              <h3 className="font-heading text-3xl md:text-4xl text-primary-950 mt-3">
                From the <em>counter.</em>
              </h3>
            </div>
            <span className="text-sm text-foreground-600">Served daily until 3 pm</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-6">
            {DRINKS.map((d) => (
              <div key={d.name} className="flex items-baseline gap-6 py-4 border-b border-background-200">
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-4 mb-1">
                    <h4 className="font-heading text-xl text-primary-950">{d.name}</h4>
                    <span className="font-heading text-lg text-accent-700">{d.price}</span>
                  </div>
                  <p className="text-sm text-foreground-700 italic">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}